import React, { useState } from "react";
import { WithTesting } from "../../../types";
import {
  AddButton,
  ColorsContainer,
  SavedColorsTitle,
  SavedColorsTitleSection,
  SavedColorsWrapper,
} from "./SavedColors.styled";
import { Icon } from "../../Icon";
import {
  addSavedColor,
  isValidColorpickerColor,
  loadSavedColors,
} from "../utils";
import { Color } from "../ColorPicker.styled";
import { SupportedColorPickerModes } from "../ColorPicker.types";
import { COLORPICKER_MODES, isWithAlphaMode } from "../ColorPicker.utils";

type SavedColorProps = WithTesting & {
  currentValue: string;
  onChange: (value: string) => void;
  predefinedColors?: string[];
  colorpickerMode?: SupportedColorPickerModes;
};

function filterPredefinedColors(
  predefinedColors: string[],
  currentMode: SupportedColorPickerModes,
) {
  // if the current mode is HEX, we need to filter out the colors that are not valid HEX
  if (!isWithAlphaMode(currentMode)) {
    return predefinedColors.filter((color) => isValidColorpickerColor(color));
  }

  return predefinedColors;
}

const SavedColors = ({
  dataTestId,
  currentValue,
  onChange,
  predefinedColors = [],
  colorpickerMode = COLORPICKER_MODES.HEX_ALPHA,
}: SavedColorProps) => {
  const [savedColors, setSavedColors] = useState(loadSavedColors());

  const onSaveColor = () => {
    const isSaved = addSavedColor(currentValue);
    if (isSaved) {
      setSavedColors((prev) => [...prev, currentValue]);
    }
  };

  const handleColorKeyDown =
    (color: string) => (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        onChange(color);
      }
    };

  const onSavedColorClick = (color: string) => () => {
    onChange(color);
  };

  const predefinedColorsFiltered = filterPredefinedColors(
    [...predefinedColors, ...savedColors],
    colorpickerMode,
  );

  return (
    <SavedColorsWrapper>
      <SavedColorsTitleSection>
        <SavedColorsTitle>Saved colors:</SavedColorsTitle>
        <AddButton
          data-testid={`save-color-${dataTestId}`}
          buttonType="secondary"
          buttonVariant="text"
          size="small"
          label="Add"
          startIcon={<Icon name="add" />}
          onClick={onSaveColor}
          tabIndex={0}
        />
      </SavedColorsTitleSection>
      <ColorsContainer>
        {predefinedColorsFiltered.map((color) => (
          // Note that color is unique so we can use it as key
          <Color
            key={color}
            color={color}
            onClick={onSavedColorClick(color)}
            clickable
            tabIndex={0}
            data-testid={color}
            onKeyDown={handleColorKeyDown(color)}
            aria-label={color}
          />
        ))}
      </ColorsContainer>
    </SavedColorsWrapper>
  );
};

export default SavedColors;
